/**
 * StartTrip.jsx - Trip planning component
 * Allows users to switch between passenger/driver modes and plan trips
 */
import React, { useState } from "react";
import axios from "axios";
import "./StartTripScreen.css";
import {
  IonContent,
  IonPage,
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonCheckbox,
  IonAlert
} from '@ionic/react';
import { arrowBack, car } from 'ionicons/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import Popup from 'reactjs-popup';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import { API_URL } from '../../index';
import { convertLocalToUTC, getCurrentLocalDate, getCurrentLocalTime } from '../../utils/dateTimeUtils';

const StartTrip = () => {
  const [isDriver, setIsDriver] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [amountOfSeats, setAmountOfSeats] = useState('1');
  const [isSeatsDropdownOpen, setIsSeatsDropdownOpen] = useState(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, startLocation, endLocation, startlongitude, startLatitude, endLongitude, endLatitude } = location.state;

  const handleErrorClose = () => setShowErrorPopup(false);
  const handleSuccessClose = () => {
    setShowSuccessPopup(false);
    navigate("/home");
  };
  const handlePostTripClick = () => setShowConfirmAlert(true);
  const handleTimeChange = (time) => setSelectedTime(time);
  const handleDateChange = (date) => setSelectedDate(date);
  const handleSeatChange = (seats) => setAmountOfSeats(seats);

  const handlePostTrip = async () => {
    setShowErrorPopup(false);
    setIsLoading(true);

    if (user.driver_id === null || user.Is_Verified === null) {
      setShowErrorPopup(true);
      setIsLoading(false);
      return;
    }

    const formattedDate = selectedDate || getCurrentLocalDate();
    const formattedTime = selectedTime || getCurrentLocalTime();
    const formattedDateTime = convertLocalToUTC(formattedDate, formattedTime);
    
    if (!formattedDateTime) {
      setShowErrorPopup(true);
      setIsLoading(false);
      return;
    }

    const requestBody = {
      driverId: user.driver_id,
      driver_origin_latitude: startLatitude,
      driver_origin_longitude: startlongitude,
      driverOriginName: startLocation,
      driver_destination_latitude: endLatitude,
      driver_destination_longitude: endLongitude,
      driverDestinationName: endLocation,
      driverTripArrival_DateTime: formattedDateTime,
      seats_available: amountOfSeats,
      costPerKM: '0.56'
    };

    try {
      await axios.post(`${API_URL}/inserttripasdriver`, requestBody);
      setIsLoading(false);
      setShowSuccessPopup(true);
    } catch (error) {
      setShowErrorPopup(true);
      setIsLoading(false);
    }
  };

  const handleSearchTrips = async () => {
    setIsLoading(true);
    setIsBtnDisabled(true);

    const searchDate = selectedDate || getCurrentLocalDate();
    // Default to start-of-day when no explicit time is provided
    const searchTime = selectedDate ? '12:20 PM' : '12:00 AM';
    const utcSearchDateTime = convertLocalToUTC(searchDate, searchTime);

    const requestBody = {
      reqStartLoc: startLocation,
      reqEndLoc: endLocation,
      reqUserID: user.user_id,
      reqStartLong: startlongitude,
      reqStartLat: startLatitude,
      reqEndLong: endLongitude,
      reqEndLat: endLatitude,
      reqArrivalDate: utcSearchDateTime
    };

    try {
      const response = await axios.post(`${API_URL}/searchresults`, requestBody);
      navigate("/findtripasrider", {
        state: {
          user,
          tripResults: response.data,
          originLocation: startLocation,
          destinationLocation: endLocation,
          selectedDate: selectedDate || getCurrentLocalDate()
        },
      });
    } catch (error) {
      console.error('Search trips error:', error);
    } finally {
      setIsLoading(false);
      setIsBtnDisabled(false);
    }
  };

  return (
    <IonPage className="trip-planner-page start-trip-page">
      {isLoading && <LoadingScreen />}
      <IonContent fullscreen className="trip-planner-content">
        <div className="content-container">
          {/* Header with Back Button */}
          <div className="user-header">
            <IonButton
              fill="clear"
              className="back-button"
              onClick={() => navigate(-1)}
            >
              <IonIcon icon={arrowBack} />
            </IonButton>
          </div>

          {/* Page Title */}
          <div className="page-title-section">
            <h2 className="page-title">Trip planner</h2>
          </div>

          {/* Location Summary Card */}
          <div className="location-card">
            <div className="location-item">
              <div className="location-icon pickup-icon"></div>
              <div className="location-details">
                <div className="location-type">
                  <IonIcon icon={car} className="car-icon" />
                  Pickup location
                </div>
                <div className="location-name">{startLocation}</div>
              </div>
            </div>

            <div className="location-item">
              <div className="location-icon dropoff-icon"></div>
              <div className="location-details">
                <div className="location-type">
                  <IonIcon icon={car} className="car-icon" />
                  Dropoff location
                </div>
                <div className="location-name">{endLocation}</div>
              </div>
            </div>
          </div>

          {/* Trip Options Card */}
          <div className="trip-options-card">
            <div className="driver-radio-group">
              <div className="radio-option">
                <div className={`radio-circle ${!isDriver ? 'checked' : ''}`}>
                  <IonCheckbox
                    checked={!isDriver}
                    onIonChange={() => setIsDriver(false)}
                    className="custom-checkbox"
                  />
                </div>
                <IonLabel className="radio-label">I am a passenger</IonLabel>
              </div>
              <div className="radio-option">
                <div className={`radio-circle ${isDriver ? 'checked' : ''}`}>
                  <IonCheckbox
                    checked={isDriver}
                    onIonChange={() => setIsDriver(true)}
                    className="custom-checkbox"
                  />
                </div>
                <IonLabel className="radio-label">I am a driver</IonLabel>
              </div>
            </div>
          </div>

          {/* Trip Details Card */}
          <div className="trip-details-card">
            <IonGrid className="details-grid">
              <IonRow>
                <IonCol>
                  <IonItem lines="none">
                    <IonLabel position="stacked" className="label">Arrival date</IonLabel>
                    <IonInput
                      type="date"
                      value={selectedDate || getCurrentLocalDate()}
                      onIonInput={e => handleDateChange(e.detail.value)}
                      className="input"
                    />
                  </IonItem>
                </IonCol>
                {isDriver && (
                  <IonCol>
                    <IonItem lines="none">
                      <IonLabel position="stacked" className="label">Arrival time</IonLabel>
                      <div className="custom-select-container">
                        <IonButton
                          fill="outline"
                          expand="block"
                          className="custom-select-button"
                          onClick={() => setIsTimeDropdownOpen(!isTimeDropdownOpen)}
                        >
                          {selectedTime || '12:00 AM'}
                          <svg className="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <polyline points="6,9 12,15 18,9"></polyline>
                          </svg>
                        </IonButton>
                        {isTimeDropdownOpen && (
                          <div className="custom-select-dropdown time-dropdown">
                            {Array.from({ length: 24 * 4 }, (_, index) => {
                              const hours = Math.floor(index / 4);
                              const minutes = (index % 4) * 15;
                              const timeString = `${hours % 12 === 0 ? 12 : hours % 12}:${minutes.toString().padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
                              return (
                                <IonButton
                                  key={index}
                                  fill="clear"
                                  expand="block"
                                  className="custom-select-option"
                                  onClick={() => {
                                    handleTimeChange(timeString);
                                    setIsTimeDropdownOpen(false);
                                  }}
                                >
                                  {timeString}
                                </IonButton>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </IonItem>
                  </IonCol>
                )}
              </IonRow>
            </IonGrid>
          </div>

          {isDriver && (
            <div className="seats-card">
              <IonItem lines="none">
                <IonLabel position="stacked" className="label">Number of seats available</IonLabel>
                <div className="custom-select-container">
                  <IonButton
                    fill="outline"
                    expand="block"
                    className="custom-select-button"
                    onClick={() => setIsSeatsDropdownOpen(!isSeatsDropdownOpen)}
                  >
                    {amountOfSeats}
                    <svg className="dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                  </IonButton>
                  {isSeatsDropdownOpen && (
                    <div className="custom-select-dropdown">
                      {['1', '2', '3'].map((seat) => (
                        <IonButton
                          key={seat}
                          fill="clear"
                          expand="block"
                          className="custom-select-option"
                          onClick={() => {
                            handleSeatChange(seat);
                            setIsSeatsDropdownOpen(false);
                          }}
                        >
                          {seat}
                        </IonButton>
                      ))}
                    </div>
                  )}
                </div>
              </IonItem>
            </div>
          )}

          {/* Action Button */}
          <div className="action-button-container">
            <IonButton
              expand="block"
              disabled={isBtnDisabled}
              className="search-trip-button"
              onClick={isDriver ? handlePostTripClick : handleSearchTrips}
            >
              {isDriver ? 'Post Trip' : 'Search Trips'}
            </IonButton>
          </div>

          <IonAlert
            isOpen={showConfirmAlert}
            onDidDismiss={() => setShowConfirmAlert(false)}
            header="Confirm Post Trip"
            message="Are you sure you want to post this trip?"
            buttons={[
              {
                text: 'Cancel',
                role: 'cancel',
                handler: () => setShowConfirmAlert(false)
              },
              {
                text: 'Confirm',
                handler: () => {
                  setShowConfirmAlert(false);
                  handlePostTrip();
                }
              }
            ]}
          />

          <Popup open={showErrorPopup} modal nested overlay={{ background: 'transparent' }}>
            <div className="booking-confirmation-modal">
              <button className="close-modal-button" onClick={handleErrorClose}>
                &times;
              </button>
              <div className="modal-header-section">Post Trip Error</div>
              <div className="modal-content-section">
                You are either not verified or do not have a driver profile setup. Please verify your profile and try again.
              </div>
              <div className="modal-actions-section">
                <IonButton className="book-now-button" onClick={handleErrorClose}>
                  Return Home
                </IonButton>
              </div>
            </div>
          </Popup>

          <Popup open={showSuccessPopup} modal nested overlay={{ background: 'transparent' }}>
            <div className="booking-confirmation-modal">
              <button className="close-modal-button" onClick={handleSuccessClose}>
                &times;
              </button>
              <div className="modal-header-section">Trip Posted Successfully!</div>
              <div className="modal-content-section">
                Your trip has been posted successfully. Other users can now see and book your trip.
              </div>
              <div className="modal-actions-section">
                <IonButton className="book-now-button" onClick={handleSuccessClose}>
                  Return Home
                </IonButton>
              </div>
            </div>
          </Popup>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default StartTrip;